import { useState, useEffect, useCallback, useRef } from "react";

/** State shape returned by resource hooks. */
export interface ResourceState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Timestamp of last successful fetch. */
  fetchedAt: number | null;
  /** Re-fetch the resource. */
  refetch: () => void;
}

/**
 * Generic hook for fetching a single API resource.
 *
 * @param fetcher - Async function that returns the data. Receives an AbortSignal.
 *                  Return null to skip (e.g., when client is not ready).
 * @param deps    - Dependency array that triggers a re-fetch when changed.
 */
export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T | null>,
  deps: React.DependencyList,
): ResourceState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetcher(controller.signal)
      .then((result) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (result !== null) {
          setData(result);
          setFetchedAt(Date.now());
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : "Unknown error";
        setError(message);
        setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    const cleanup = fetchData();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [fetchData]);

  return { data, loading, error, fetchedAt, refetch: fetchData };
}
